import React, { useState, useEffect } from 'react';
import { SocialPost } from '../../../core/types/social';
import { X, Send, Loader2, Heart } from 'lucide-react';
import { interactionService, Comment } from '../../../core/services/firebase/interactionService';
import { useAuth } from '../../auth/AuthContext';
import { useSocial } from '../SocialContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CommentsModalProps {
  post: SocialPost;
  authorName?: string;
  authorAvatar?: string;
  onClose: () => void;
  onCommentAdded?: () => void;
}

export const CommentsModal: React.FC<CommentsModalProps> = ({
  post,
  authorName,
  authorAvatar,
  onClose,
  onCommentAdded
}) => {
  const { user } = useAuth();
  const { profile } = useSocial();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadComments();
  }, [post.id]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const data = await interactionService.getPostComments(post.id);
      setComments(data);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !profile || !newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await interactionService.addComment(
        post.id,
        user.uid,
        profile.handle,
        profile.displayName,
        newComment.trim(),
        profile.avatarUrl
      );

      setNewComment('');
      await loadComments();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-midnight/80 backdrop-blur-md z-[500] flex items-center justify-center p-4">
      <div className="bg-brand-midnight rounded-[2.5rem] border border-white/10 max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-8 py-6 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30">Comentários</h3>
          <button
            onClick={onClose}
            aria-label="Fechar comentários"
            className="p-2 hover:bg-white/5 rounded-xl transition-colors text-white/40 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Post Preview */}
        <div className="px-8 py-6 border-b border-white/5 bg-white/[0.02]">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex-shrink-0 overflow-hidden border border-white/10">
              {authorAvatar ? (
                <img src={authorAvatar} alt={authorName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20 font-black">
                  {(authorName || post.authorId)[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-white text-xs uppercase tracking-tight">{authorName || post.authorId}</p>
              {post.content && (
                <p className="text-sm text-white/40 line-clamp-2 mt-1 font-medium italic">{post.content}</p>
              )}
            </div>
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-hide">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-brand-gold" size={32} />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-20 text-white/20">
              <p className="text-sm font-black uppercase tracking-widest">Nenhum comentário ainda.</p>
              <p className="text-xs mt-2 uppercase tracking-widest opacity-50">Seja o primeiro a participar!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex-shrink-0 overflow-hidden border border-white/10">
                  {comment.userAvatar ? (
                    <img src={comment.userAvatar} alt={comment.userName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20 text-xs font-black">
                      {comment.userName[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/5">
                    <p className="font-black text-white text-[11px] uppercase tracking-tight mb-1">{comment.userName}</p>
                    <p className="text-sm text-white/70 leading-relaxed font-medium">{comment.content}</p>
                  </div>
                  <p className="text-[10px] text-white/20 font-black uppercase tracking-widest mt-2 px-1">
                    {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true, locale: ptBR })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Comment Input */}
        {user && profile ? (
          <form onSubmit={handleSubmit} className="p-8 border-t border-white/10 bg-white/[0.02]">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex-shrink-0 overflow-hidden border border-white/10">
                {profile.avatarUrl ? (
                  <img src={profile.avatarUrl} alt={profile.displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20 font-black">
                    {profile.displayName[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 flex gap-3">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escreva algo..."
                  aria-label="Escrever comentário"
                  className="flex-1 px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-4 focus:ring-brand-gold/10 outline-none transition-all placeholder:text-white/20 font-medium"
                  disabled={submitting}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  aria-label="Enviar comentário"
                  className="px-8 py-4 bg-brand-gold text-brand-midnight rounded-2xl font-black uppercase tracking-widest text-xs shadow-glow-gold hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="p-10 border-t border-white/10 text-center text-white/20 text-xs font-black uppercase tracking-widest">
            Conecte-se para participar da conversa
          </div>
        )}
      </div>
    </div>
  );
};
